import { useSelector, useDispatch, batch } from 'react-redux';
import React, { Suspense, useState, useEffect, useRef, useCallback } from 'react';
import { isBrowser } from 'ws-scripts/modules/environment';
import { trackEvent } from 'ddc-track-event';
import { Common } from 'ws-scripts/modules/common';
import { usePrefs } from 'wsm-common-data';
import { ErrorBoundary } from 'wsm-error-boundary';
import debounce from 'lodash.debounce';

import { setModalSettings } from '../actions/modalSettings';
import { setRenderSettings } from '../actions/renderSettings';
import { wiapiMediaUpdated, addFailedImages } from '../actions/media';
import { setIsBeingDragged } from '../actions/isBeingDragged';
import LoadingSpinner from '../components/LoadingSpinner';
import Slider from '../components/Slider';
import PhotoSwipeGalleryButton from '../components/photoswipe/PhotoSwipeGalleryButton';
import SingleImage from '../components/SingleImage';
import Cta from '../components/Cta';
import viewportSize from '../helpers/viewportSize';
import {
	replaceCarouselImageWithHTML,
	resetWIAPIContent
} from '../helpers/wiapi-html';
import { handleGalleryToolbarVisibility } from '../helpers/visibility';
import { WiapiImageInsertTracker } from '../helpers/wiapi-tracking';
import { NO_PHOTOS_IMAGE } from '../helpers/no-photos';

import './Widget.scss';
import { getMediaDisplay } from '../helpers/getMediaDisplay';
import { clearImageCaches } from '../helpers/imageResizer';
// import PanoramicModal from '../components/panoramic/PanoramicModal';

const PanoramicModal = React.lazy(() =>
	import(
		/* webpackChunkName: "PanoramicModal" , webpackPrefetch: true */ '../components/panoramic/PanoramicModal'
	)
);

const Widget = () => {
	const prefs = usePrefs()
	const dispatch = useDispatch();
	const {
		media,
		widgetInfo,
		renderSettings,
		modalSettings,
		isBeingDragged
	} = useSelector((state) => ({
		media: state.media,
		widgetInfo: state.widgetInfo,
		renderSettings: state.renderSettings,
		modalSettings: state.modalSettings,
		isBeingDragged: state.isBeingDragged
	}));

	// Action dispatchers
	const updateWiapiMedia = useCallback((
		galleryContent,
		offsetSharedVehicleImageByOne,
		useStockPhotosForOffsiteInventory
	) => {
		dispatch(
			wiapiMediaUpdated(
				galleryContent,
				offsetSharedVehicleImageByOne,
				useStockPhotosForOffsiteInventory
			)
		);
	}, [dispatch]);

	const updateModalSettings = useCallback((props) => {
		dispatch(setModalSettings(props));
	}, [dispatch]);

	const updateRenderSettings = useCallback((props) => {
		dispatch(setRenderSettings(props));
	}, [dispatch]);

	const updateIsBeingDragged = useCallback((dragging) => {
		dispatch(setIsBeingDragged(dragging));
	}, [dispatch]);

	const addFailedImage = useCallback((
		imageSrc,
		offsetSharedVehicleImageByOne,
		useStockPhotosForOffsiteInventory
	) => {
		dispatch(
			addFailedImages(
				[imageSrc],
				offsetSharedVehicleImageByOne,
				useStockPhotosForOffsiteInventory
			)
		);
	}, [dispatch]);

	// Component state
	const [state, setState] = useState({
		disableAnimation: false,
		// if the imageDelayLoad pref is set, set the loading delay to true
		loadingDelayed: +prefs.imageDelayLoad > 0,
		// for desktop SSR, if multiple images we use this css class
		desktopSSRMultiImageClass: 'multi-image-desktop',
		sliderHeight: null,
		is360ModalOpen: false,
		isPanoramicImg: false,
		panoramicImgList: [],
		panoramicImageSrc: null
	});

	// Refs
	const delayTimer = useRef(null);
	const wiapiTracker = useRef(null);
	const widgetElement = useRef(null);
	const sliderCmp = useRef(null);
	const debouncedResize = useRef(null);

	// Refs mirroring latest props/state to avoid stale closures
	const prefsRef = useRef(prefs);
	const widgetInfoRef = useRef(widgetInfo);
	const modalSettingsRef = useRef(modalSettings);
	const renderSettingsRef = useRef(renderSettings);
	const mediaRef = useRef(media);
	const updateWiapiMediaRef = useRef(updateWiapiMedia);
	const updateModalSettingsRef = useRef(updateModalSettings);
	const updateRenderSettingsRef = useRef(updateRenderSettings);
	useEffect(() => {
		prefsRef.current = prefs;
		widgetInfoRef.current = widgetInfo;
		modalSettingsRef.current = modalSettings;
		renderSettingsRef.current = renderSettings;
		mediaRef.current = media;
		updateWiapiMediaRef.current = updateWiapiMedia;
		updateModalSettingsRef.current = updateModalSettings;
		updateRenderSettingsRef.current = updateRenderSettings;
	}, [prefs, widgetInfo, modalSettings, renderSettings, media, updateWiapiMedia, updateModalSettings, updateRenderSettings]);

	// Refs for panoramic state (no pending flag)
	const isPanoramicRef = useRef(false);
	const panoramicSrcRef = useRef(null);
	useEffect(() => {
		isPanoramicRef.current = state.isPanoramicImg;
		panoramicSrcRef.current = state.panoramicImageSrc;
	}, [state.isPanoramicImg, state.panoramicImageSrc]);

	// Initialize refs - exactly match constructor logic
	useEffect(() => {
		if (isBrowser) {
			wiapiTracker.current = new WiapiImageInsertTracker();
			widgetElement.current = document.querySelector('[data-widget-name="ws-vehicle-media"]');
		}
	}, []);

	// Methods
	const setRenderSettingsMethod = useCallback(() => {
		const { viewportWidth, viewportHeight } = viewportSize();
		const innerWidth = viewportWidth;
		const innerHeight = viewportHeight;

		const { threeUpDisplay, imageMode, responsiveImage, numSlides } =
			getMediaDisplay({
				prefs,
				imagesToDisplay: media?.images,
				deviceType: widgetInfo?.deviceType
			});

		// Get previous state
		let { currentIndex = 0 } = renderSettings;

		// If we've got no overflow on a 3-up display
		if (threeUpDisplay && numSlides <= 3) {
			// Reset slides to beginning to show all in 3-up
			currentIndex = 0;

			// Don't allow the gallery to change the slide index
			updateModalSettings({ persistIndex: false });
		} else {
			// Allow the current gallery slide to be shown first when returning to vehicle page
			updateModalSettings({ persistIndex: true });
		}

		updateRenderSettings({
			innerWidth,
			innerHeight,
			threeUpDisplay,
			imageMode,
			responsiveImage,
			currentIndex
		});
	}, [prefs, media, widgetInfo, renderSettings, updateModalSettings, updateRenderSettings]);

	const getMaxVisibleHeight = useCallback(() => {
		const sliderEle = sliderCmp.current?.sliderRef?.current;
		const visibleSlides = sliderEle?.querySelectorAll('.slide-visible');
		const visibleHeights = [];

		visibleSlides?.forEach((slide) => {
			if (slide?.clientHeight) {
				visibleHeights.push(slide?.clientHeight);
			}
		});

		return visibleHeights.length ? Math.max(...visibleHeights) : null;
	}, []);

	const updateSliderHeight = useCallback(() => {
		const sliderHeight = getMaxVisibleHeight();
		setState(prevState => ({ ...prevState, sliderHeight }));
	}, [getMaxVisibleHeight]);

	const debounceUpdateSliderHeight = useRef(debounce(() => {
		updateSliderHeight();
	}, 200));

	// Update the ref when updateSliderHeight changes
	useEffect(() => {
		debounceUpdateSliderHeight.current = debounce(() => {
			updateSliderHeight();
		}, 200);
	}, [updateSliderHeight]);

	const onResize = useCallback(() => {
		clearImageCaches();
		batch(() => {
			setRenderSettingsMethod();
			updateSliderHeight();
		});
	}, [setRenderSettingsMethod, updateSliderHeight]);

	const trackGa = useCallback((trackingObj) => {
		const defaultTrackingObj = {
			widgetState: `default (${renderSettings?.threeUpDisplay ? 'tile view' : 'full view'})`
		};

		trackEvent('ws-vehicle-media', widgetInfo?.windowId, {
			...defaultTrackingObj,
			...trackingObj
		});
	}, [renderSettings?.threeUpDisplay, widgetInfo?.windowId]);

	const open360Modal = useCallback(() => {
		setState(prevState => ({ ...prevState, is360ModalOpen: true }));
	}, []);

	const close360Modal = useCallback(() => {
		setState(prevState => ({ ...prevState, is360ModalOpen: false }));
	}, []);

	// WIAPI method - stable handler reads latest values from refs
	const onWiapiVehicleMediaUpdated = useCallback(() => {
		const prefsVal = prefsRef.current || {};
		const widgetInfoVal = widgetInfoRef.current || {};
		const modalSettingsVal = modalSettingsRef.current || {};
		const renderSettingsVal = renderSettingsRef.current || {};
		const mediaVal = mediaRef.current || {};

		const {
			offsetSharedVehicleImageByOne,
			useStockPhotosForOffsiteInventory
		} = prefsVal || {};
		const {
			vehicle: { vin } = {}
		} = widgetInfoVal || {};

		// Return early if the WIAPI is not available.
		if (!(
			DDC &&
			DDC.PrivateAPI &&
			typeof DDC.PrivateAPI.getGalleryContent === 'function'
		)) {
			return;
		}

		// get the gallery content for the current vehicle
		const galleryContent = DDC.PrivateAPI.getGalleryContent(
			'vehicle-media',
			vin
		);

		// return early if no content was found
		if (!galleryContent) {
			return;
		}

		// return early if no media existed in the content
		if (!(
			galleryContent.primary.length > 0 ||
			galleryContent.secondary.length > 0 ||
			galleryContent.last.length > 0
		)) {
			return;
		}

		wiapiTracker.current.onImageLoad(
			modalSettingsVal?.modalIsOpen,
			modalSettingsVal?.currentIndex,
			renderSettingsVal?.currentIndex
		);

		const oldIndex = renderSettingsVal?.currentIndex || 0;
		const oldImagesToDisplay = mediaVal?.imagesToDisplay;

		batch(() => {
			setState((prevState) => ({
				...prevState,
				disableAnimation: true
			}));

			// insert the WIAPI media into the gallery
			updateWiapiMediaRef.current(
				galleryContent,
				offsetSharedVehicleImageByOne,
				useStockPhotosForOffsiteInventory
			);
		});

		// Recompute panoramic list immediately
		if (isPanoramicRef.current && panoramicSrcRef.current) {
			const currentImagesToDisplay = mediaRef.current?.imagesToDisplay;
			const newPanoramicImgList = currentImagesToDisplay?.filter(
				(img) => img.src !== panoramicSrcRef.current
			);
			setState((prevState) => ({
				...prevState,
				panoramicImgList: newPanoramicImgList
			}));
		}

		// If the user hasn't interacted with the widget, go to the first primary image inserted (index = 0).
		// If they have interacted with the widget, update the index so they are seeing the same image as before.
		let newIndex = 0;
		if (modalSettingsVal?.modalIsOpen || renderSettingsVal?.currentIndex !== 0) {
			const newImagesToDisplay = mediaRef.current?.imagesToDisplay;
			const foundIndex = newImagesToDisplay?.findIndex(
				(elem) => elem.src === oldImagesToDisplay?.[oldIndex]?.src
			);
			newIndex = foundIndex !== -1 ? foundIndex : 0;
		}

		batch(() => {
			updateRenderSettingsRef.current({ currentIndex: newIndex });
			updateModalSettingsRef.current({ currentIndex: newIndex });
			setState((prevState) => ({
				...prevState,
				disableAnimation: false
			}));
		});

		// Workaround for zero-height issue
		let elapsedTime = 0;
		const interval = 250;
		const intervalId = setInterval(() => {
			elapsedTime += interval;
			if (elapsedTime >= 10000) {
				clearInterval(intervalId);
			}
			if (isBrowser) {
				const elem = widgetElement.current?.querySelector('.slider-frame');
				if (elem && elem.offsetHeight === 0) {
					window.dispatchEvent(new Event('resize'));
				}
			}
		}, interval);

		// if imageDelayLoad preference is set, clear the delayTimer and toggle the loading delay
		if (+prefsVal?.imageDelayLoad > 0) {
			clearTimeout(delayTimer.current);
			setState((prevState) => ({
				...prevState,
				loadingDelayed: false
			}));
		}
	}, []);

	// componentDidMount equivalent - exactly match class component
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		// If imageDelayLoad preference is set, start the delay timer
		if (+prefs.imageDelayLoad > 0) {
			delayTimer.current = setTimeout(() => {
				setState((prevState) => ({
					...prevState,
					loadingDelayed: false
				}));
			}, +prefs.imageDelayLoad);
		}

		const { deviceType } = widgetInfo || {};
		const { images } = media || {};
		const isMobile = deviceType === 'MOBILE';

		handleGalleryToolbarVisibility(images, 0, isMobile);
		setRenderSettingsMethod();
		updateModalSettings({ modalIsOpen: false });

		if (!debouncedResize.current) {
			/* eslint no-undef: 0 */
			debouncedResize.current = _.debounce(
				onResize,
				deviceType === 'MOBILE' ? 250 : 100
			);
		}
		window.addEventListener('resize', debouncedResize.current, false);

		// Attach WIAPI listener once
		window.addEventListener('wiapiVehicleMediaUpdated', onWiapiVehicleMediaUpdated);

		// check if WIAPI media is available on mount
		onWiapiVehicleMediaUpdated();

		// check if exist any 360 image on last index
		const [lastImageObj] = images?.slice(-1);
		const img360 = new Image();
		img360.src = lastImageObj?.src;
		img360.onload = () => {
			const { naturalWidth: w, naturalHeight: h } = img360;
			const ratio = w / 2;
			if (ratio >= h) {
				const newImages = [...images];
				newImages.pop();
				setState((prevState) => ({
					...prevState,
					panoramicImgList: newImages,
					isPanoramicImg: true,
					panoramicImageSrc: lastImageObj?.src
				}));
			}
		};

		// now on browser, we don't need this class (client will auto-adjust)
		setState((prevState) => ({
			...prevState,
			desktopSSRMultiImageClass: ''
		}));

		// Cleanup function - exactly match componentWillUnmount
		return () => {
			window.removeEventListener('resize', debouncedResize.current, false);
			window.removeEventListener('wiapiVehicleMediaUpdated', onWiapiVehicleMediaUpdated);
			if (+prefs.imageDelayLoad > 0) {
				clearTimeout(delayTimer.current);
			}
		};
	}, []); // Empty dependency array for mount/unmount only

	// Extract props from Redux state
	const { videos, videoOrder } = media || {};
	const { deviceType, pathParameters, parameterMap } = widgetInfo || {};
	const {
		galleryIconColor,
		ctaHref1,
		ctaIcon1,
		ctaBtnStyle,
		ctaLabel1,
		ctaIsVehicleLink1,
		ctaIsDialogLink1,
		ctaDialogTitle1,
		ctaDialogWidth1,
		offsetSharedVehicleImageByOne,
		useStockPhotosForOffsiteInventory
	} = prefs || {};
	const {
		innerHeight,
		innerWidth,
		imageMode,
		responsiveImage,
		threeUpDisplay,
		currentIndex,
		preloadImages
	} = renderSettings || {};
	const { sliderHeight } = state;
	const isMobile = deviceType === 'MOBILE';
	const { _vehicleMediaSSR } = parameterMap || {};

	return (
		<ErrorBoundary newRelicPageAction="ws-vehicle-media: error boundary">
			<Common
				render={({ sitemap, labels, flags, prefs: commonPrefs }) => {
					// TODO: remove condition once/if we keep SSR permanent
					const serverSideRender =
						_vehicleMediaSSR === 'true' ||
						flags['ws-vehicle-media-server-render'];

					const idcImgAttrFlag =
						flags['ws-vehicle-media-toggle-idc-image-attributes'];

					if (state.loadingDelayed || (!serverSideRender && !innerHeight)) {
						return <LoadingSpinner />;
					}

					// Extract imagesToDisplay
					const imagesToDisplay = media?.imagesToDisplay;
					const shouldShow360Modal = isBrowser && commonPrefs.show360Image === 'true';
					const { panoramicImgList, isPanoramicImg } = state;

					// Use the state panoramic list (no render-time recompute)
					const imageList = isPanoramicImg ? panoramicImgList : imagesToDisplay;

					const galleryProps = {
						labels,
						images: imageList,
						widgetInfo,
						modalSettings,
						videos,
						videoOrder,
						isBeingDragged,
						disableAnimation: state.disableAnimation,
						sliderHeight,
						// renderSettings,
						currentIndex,
						innerHeight,
						innerWidth,
						imageMode,
						responsiveImage,
						preloadImages,
						threeUpDisplay,
						updateRenderSettings,
						updateModalSettings,
						updateIsBeingDragged,
						updateSliderHeight: debounceUpdateSliderHeight.current,

						// Prefs for PhotoSwipe
						galleryIconColor,
						onCurrentIndexChange: (newIndex, cb) => {
							if (newIndex !== renderSettings?.currentIndex) {
								updateRenderSettings({ currentIndex: newIndex });
							}
							if (cb) cb(newIndex);
						},
						onPhotoClick:
							imagesToDisplay?.length > 0 && imagesToDisplay[0]?.src !== NO_PHOTOS_IMAGE.src
								? (clickedIndex = renderSettings?.currentIndex) => {
									const item = imagesToDisplay[clickedIndex];
									updateIsBeingDragged(false);
									trackGa({
										dataToExcludeFromWidgetUserEvent: {
											currentItem: currentIndex + 1,
											totalItems: imagesToDisplay.length
										},
										element: 'Photo Carousel',
										result: item.type === 'html' ? 'Activates dynamic gallery content' : 'Opens gallery modal'
									});
									updateRenderSettings({ currentIndex: clickedIndex });
									if (item.type === 'html') {
										resetWIAPIContent(widgetElement.current);
										replaceCarouselImageWithHTML(widgetElement.current, item, isMobile);
									} else {
										updateModalSettings({
											modalIsOpen: true,
											videoIsOpen: false,
											spin360IsOpen: false,
											currentIndex: clickedIndex
										});
									}
								}
								: () => {},
						trackGa,
						onImageError: (originalSrc, _actualSrc) => {
							addFailedImage(
								originalSrc,
								offsetSharedVehicleImageByOne,
								useStockPhotosForOffsiteInventory
							);
						},
						open360Modal
					};

					const slider = (
						<Slider ref={sliderCmp} {...galleryProps} idcImgAttrFlag={idcImgAttrFlag} />
					);

					const photoSwipeGalleryButton = (
						<PhotoSwipeGalleryButton {...galleryProps} {...{ isMobile }} isPanoramicImg={isPanoramicImg} />
					);

					const params = new Map();
					if (ctaIsVehicleLink1 === 'true') {
						params.set('itemId', pathParameters.vehicleId);
					}

					const cta1 = (
						<Cta
							btnStyle={ctaBtnStyle}
							href={sitemap.get(ctaHref1)}
							icon={ctaIcon1}
							label={ctaLabel1}
							params={params}
							dialog={ctaIsDialogLink1 === 'true'}
							dialogTitle={ctaDialogTitle1}
							dialogWidth={ctaDialogWidth1}
							isMobile={isMobile}
							trackGa={trackGa}
							key="CTA1"
						/>
					);

					const ctaList = [cta1];
					const ctaContainerClasses = ['cta-container', 'd-flex', 'd-print-none', 'position-absolute'];
					if (!isMobile) ctaContainerClasses.push('my-4');

					const ctas = ctaList.join('').length ? (
						<div className={ctaContainerClasses.join(' ')}>{ctaList}</div>
					) : null;

					const firstImage = imagesToDisplay?.[0];
					const [lastImage] = imagesToDisplay?.slice(-1) || [];
					const singleImage = !isMobile && isBrowser ? (
						<SingleImage
							image={firstImage ? firstImage.src : ''}
							uuid={pathParameters.vehicleId}
							onClick={() => {
								updateIsBeingDragged(false);
								trackGa({
									element: 'Photo',
									result: 'Opens gallery modal',
									widgetState: firstImage?.type === 'html' ? 'dynamic content (thumbnail)' : 'default (thumbnail)'
								});
								updateModalSettings({
									modalIsOpen: true,
									videoIsOpen: false,
									spin360IsOpen: false,
									persistIndex: false,
									currentIndex: 0
								});
							}}
							trackGa={trackGa}
						/>
					) : null;

					let mediaContent = (
						<div className="media-content" data-location="vehicle-media-container">
							{ctas}
							{slider}
							{singleImage}
							{photoSwipeGalleryButton}
							{shouldShow360Modal && (
								<Suspense fallback={<div />}>
									<PanoramicModal isOpen={state.is360ModalOpen} closeModal={close360Modal} isMobile={isMobile} lastImage={lastImage} />
								</Suspense>
							)}
						</div>
					);

					if (!isMobile) {
						mediaContent = (
							<div
								className={`media-content ${imagesToDisplay?.length > 1 ? state.desktopSSRMultiImageClass : ''}`}
								data-location="vehicle-media-container"
							>
								{ctas}
								{slider}
								{singleImage}
								{photoSwipeGalleryButton}
								{shouldShow360Modal && (
									<Suspense fallback={<div />}>
										<PanoramicModal isOpen={state.is360ModalOpen} closeModal={close360Modal} isMobile={isMobile} lastImage={lastImage} />
									</Suspense>
								)}
							</div>
						);
					}
					return mediaContent;
				}}
			/>
		</ErrorBoundary>
	);
};

export default Widget;
